import { getRequestInstance } from "api/services/utils";
import { RequestTypes } from "api/services/enums";
import {
  DocumentListByIdResponse,
  ICreateDocumentListArgs,
  IDocumentListArgs,
  IDocumentListModel,
  IDocumentListResponse,
  IDocumentListUpdateArgs,
  IDocumentModel,
} from "api/services/library/document-list";
import {
  HrDocumentListCreateDocument,
  HrDocumentListDeleteDocument,
  HrDocumentListGetByIdDocument,
  HrDocumentListPaginatedDocument,
  HrDocumentListListDocument,
  HrDocumentListUpdateDocument,
} from "api/generated";
import { Optional } from "config";
import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";

export async function getHrDocumentListPaginated(
  variables: IDocumentListArgs
): Promise<IDocumentListResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrDocumentListPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.hrDocumentListPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createHrDocumentList(
  variables: ICreateDocumentListArgs
): Promise<Optional<Pick<IDocumentListModel, "documentListId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrDocumentListCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict all document list queries so they refetch with the new item
    executeEviction(client, {
      typename: "HrDocumentList",
      fieldNames: ["hrDocumentListPaginated", "hrDocumentListList"],
    });

    return res?.data?.hrDocumentListCreate;
  } catch (error) {
    throw error;
  }
}

export async function getHrDocumentList(
  documentListId: number
): Promise<DocumentListByIdResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrDocumentListGetByIdDocument,
      variables: {
        documentListId,
      },
      fetchPolicy: "cache-first",
    });

    return res?.data?.hrDocumentListGetById;
  } catch (error) {
    throw error;
  }
}

export async function updateHrDocumentList(
  variables: IDocumentListUpdateArgs
): Promise<Optional<Pick<IDocumentListModel, "documentListId">>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrDocumentListUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    // Evict the updated entity and all lists containing it
    executeEviction(client, {
      typename: "HrDocumentList",
      id: variables.documentListId.toString(),
      fieldNames: [
        "hrDocumentListPaginated",
        "hrDocumentListList",
        "hrDocumentListGetById",
      ],
    });

    return res?.data?.hrDocumentListUpdate;
  } catch (error) {
    throw error;
  }
}

export async function deleteHrDocumentList(
  documentListId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: HrDocumentListDeleteDocument,
      variables: {
        documentListId,
      },
      fetchPolicy: "no-cache",
    });

    // Evict the deleted entity and all related data
    executeEviction(client, {
      typename: "HrDocumentList",
      id: documentListId.toString(),
      fieldNames: [
        "hrDocumentListPaginated",
        "hrDocumentListList",
        "hrDocumentListGetById",
      ],
    });

    return Boolean(res?.data?.hrDocumentListDelete);
  } catch (error) {
    throw error;
  }
}

export async function getHrDocumentLists(): Promise<
  (Pick<IDocumentListModel, "name" | "documentListId"> & {
    documents: Pick<IDocumentModel, "documentId" | "name">[];
  })[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: HrDocumentListListDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.hrDocumentListList ?? [];
  } catch (error) {
    throw error;
  }
}
